/**
 * Authentication Manager - SIMPLIFIED
 * Focus: Login/logout state management, avoid race conditions, support multi-account
 */

import { LoginCredentials, AuthResult, User, UserRole, UserPreferences, Tenant, AuthErrorType, LoginSuccessPayload, TokenPair } from '../../types/auth';
import { StoredAccount } from '../../features/auth/utils/accountStorage';
import { logger } from '../../utils/logger';
import store from '../../store';
import {
  startLogin,
  startLogout,
  loginSuccess,
  loginFailure,
  logoutSuccess,
  logoutFailure,
  initializeFromStorage,
  initializationSuccess,
  initializationFailure,
  startAccountSwitchWorkflow,
  accountSwitchSuccess,
  accountSwitchFailure,
  accountRemoved
} from '../../store/slices/authSlice';
import { tokenManager } from './tokenManager';

export interface IAuthManager {
  // Core authentication
  login(credentials: LoginCredentials): Promise<AuthResult>;
  loginWithGoogle(token: string): Promise<AuthResult>;
  logout(): Promise<AuthResult>;
  
  // Session management
  initializeSession(): Promise<AuthResult>;
  
  // Multi-account management
  getAvailableAccounts(): Promise<StoredAccount[]>;
  switchAccount(accountId: string): Promise<AuthResult>;
  addAccount(credentials: LoginCredentials, options?: { rememberMe?: boolean; persistenceDays?: number }): Promise<AuthResult>;
  removeAccount(accountId: string): Promise<AuthResult>;
  getCurrentAccountId(): string | null;
  
  // State management
  getCurrentUser(): User | null;
  isAuthenticated(): boolean;
  isInitialized(): boolean;
}

/**
 * Simple Authentication Manager Implementation
 */
export class AuthManager implements IAuthManager {
  private loginMutex = false; // Prevent race conditions

  /**
   * Shared function to retrieve user info and store in Redux
   * Used by both standard login and Google login
   * Made public for manual refresh functionality
   */
  async retrieveAndStoreUserInfo(accessToken: string, context: 'login' | 'accountSwitch' | 'initialization' = 'login'): Promise<void> {
    logger.auth('Retrieving user info from API', { context });
    
    try {
      logger.auth('Retrieving complete user info from API');
      
      // Use apiClient instead of direct axios to get automatic 401 handling
      const { apiClient } = await import('../../utils/apiClient');
      const apiConstants = await import('../../constants/apiConstants');
    
    // Call the user info API to get complete user data with roles and logins
    const userInfoResponse = await apiClient.get(
      `${apiConstants.API_USERS_ME}`,
      {
        params: {
          partitionKey: "sys:smart_matrix"
        }
      }
    );

    const userInfoResult = userInfoResponse.data;
    
    logger.auth('Raw API response structure', {
      succeeded: userInfoResult.succeeded,
      hasData: !!userInfoResult.data,
      dataKeys: userInfoResult.data ? Object.keys(userInfoResult.data) : [],
      fullResponse: userInfoResult
    });
    
    if (!userInfoResult.succeeded || !userInfoResult.data) {
      throw new Error('Failed to retrieve user info: Invalid response from user info API');
    }

    // The API might return data in different structures, check both
    const tokenContent = userInfoResult.data.tokenContent;
    
    if (!tokenContent) {
      throw new Error('Failed to retrieve user info: No tokenContent found in API response');
    }
    
    logger.auth('Token content from /me API', {
      tokenContent,
      availableFields: Object.keys(tokenContent),
      userDetails: {
        loginNameIdentifier: tokenContent.loginNameIdentifier,
        userNameIdentifier: tokenContent.userNameIdentifier,
        email: tokenContent.email,
        name: tokenContent.name,
        roles: tokenContent.roles?.length || 0
      }
    });

    // Extract user ID from token content
    const realUserId = tokenContent.loginNameIdentifier || tokenContent.userNameIdentifier || tokenContent.email;
    
    if (!realUserId) {
      throw new Error('User info API did not return a valid user identifier');
    }
    
    // NEW: Extract numeric user ID and login name directly from JWT token
    let numericUserIdFromToken = null;
    let loginNameFromToken = null;
    try {
      const tokenPayload = JSON.parse(atob(accessToken.split('.')[1]));
      numericUserIdFromToken = tokenPayload.user_id;
      // Use the correct claim name from backend: login_name_identifier
      loginNameFromToken = tokenPayload.login_name_identifier || tokenPayload.unique_name || tokenPayload.sub;
      
      logger.auth('Extracted data from JWT token', { 
        numericUserIdFromToken, 
        loginNameFromToken,
        tokenPayload: Object.keys(tokenPayload),
        fullTokenPayload: tokenPayload
      });
      
      // Ensure it's a valid numeric ID
      if (numericUserIdFromToken && !isNaN(Number(numericUserIdFromToken))) {
        numericUserIdFromToken = Number(numericUserIdFromToken);
        logger.auth('Numeric user ID validated from JWT token', { numericUserIdFromToken });
      } else {
        logger.warn('JWT token user_id is not a valid number', { 
          user_id: numericUserIdFromToken, 
          type: typeof numericUserIdFromToken 
        });
        numericUserIdFromToken = null;
      }
    } catch (tokenParseError) {
      logger.warn('Failed to parse JWT token for user ID', tokenParseError);
    }
    
    // If we need complete user data with logins, call the by-login-name endpoint
    // Use the login name from JWT token if available, otherwise fall back to realUserId
    let completeUserData = null;
    const loginNameToUse = loginNameFromToken || realUserId;
    
    try {
      logger.auth('Fetching complete user data with logins from by-login-name endpoint', {
        loginNameToUse,
        source: loginNameFromToken ? 'jwt_token' : 'token_content'
      });
      
      const completeUserResponse = await apiClient.get(
        `${apiConstants.API_USERS_BY_LOGIN_NAME}`,
        {
          params: {
            partitionKey: "sys:smart_matrix",
            loginName: loginNameToUse
          }
        }
      );

      if (completeUserResponse.data.succeeded && completeUserResponse.data.data?.user) {
        completeUserData = completeUserResponse.data.data.user;
        logger.auth('Complete user data retrieved successfully', {
          userId: completeUserData.id,
          userName: completeUserData.userName,
          loginsCount: completeUserData.logins?.length || 0,
          rolesCount: completeUserData.roles?.length || 0
        });

        // Sync complete user data (including profilePictureUrl) to userSlice
        // so the Header/Avatar can render immediately without waiting for loadUserDataThunk
        try {
          const { setUser } = await import('../../store/slices/userSlice');
          store.dispatch(setUser({ user: completeUserData, error: '' }));
          logger.auth('User data synced to userSlice');
        } catch (syncError) {
          logger.warn('Failed to sync user data to userSlice', syncError);
        }
      }
    } catch (completeDataError) {
      logger.warn('Failed to fetch complete user data, using token content only', completeDataError);
    }

    // Use complete user data if available, otherwise use token content
    const userData = completeUserData || {
      id: null, // Will be resolved below
      userName: tokenContent.userNameIdentifier,
      displayName: tokenContent.name,
      email: tokenContent.email,
      roles: tokenContent.roles || [],
      logins: [], // No logins from token content
      // Login tracking fields from token content
      lastLoginAt: tokenContent.lastLoginAt,
      previousLoginAt: tokenContent.previousLoginAt,
      lastLoginProvider: tokenContent.lastLoginProvider,
      loginCount: tokenContent.loginCount || 0
    };
    
    // Extract the user ID - prioritize numeric user ID from JWT token
    let finalUserId = numericUserIdFromToken || userData.id;
    
    if (!finalUserId) {
      // Try to resolve numeric user ID from the identifier
      if (realUserId && typeof realUserId === 'string' && isNaN(Number(realUserId))) {
        // If we have complete user data, use its ID
        if (completeUserData && completeUserData.id) {
          finalUserId = completeUserData.id;
        } else {
          // Create a fallback ID based on the identifier
          const { safeParseUserId } = await import('../../utils/userIdValidation');
          finalUserId = safeParseUserId(realUserId);
        }
      } else {
        finalUserId = realUserId;
      }
    }
    
    logger.auth('Final user ID resolution', {
      finalUserId,
      type: typeof finalUserId,
      isNumeric: !isNaN(Number(finalUserId)),
      source: numericUserIdFromToken ? 'jwt_token' : (completeUserData ? 'complete_user_data' : 'token_content'),
      numericUserIdFromToken,
      completeUserDataId: completeUserData?.id,
      realUserId
    });
    
    // Validate required user data
    if (!finalUserId) {
      throw new Error('Could not resolve a valid user ID');
    }
    // Email is optional - some users may not have an email address
    if (!userData.displayName && !userData.userName) {
      throw new Error('User info API did not return name');
    }

    // Ensure the user ID is numeric
    const numericUserId = Number(finalUserId);
    if (isNaN(numericUserId)) {
      throw new Error(`User ID must be numeric, got: ${finalUserId} (${typeof finalUserId})`);
    }

    // Filter out deleted roles
    const activeRoles = (userData.roles || []).filter((role: any) => {
      if (typeof role === 'string') return true; // Role codes from token content
      return role && !role.isDeleted && !role.deletedAt; // Role objects from complete data
    });
    
    // Filter out deleted logins
    const activeLogins = (userData.logins || []).filter((login: any) => 
      login && !login.isDeleted && !login.deletedAt
    );

    // Create user object with complete data including roles and logins
    const user: User = {
      id: String(numericUserId),
      email: userData.email || '', // Handle null/empty email
      name: userData.displayName || userData.userName || userData.email || `User ${numericUserId}`, // Fallback to user ID if no name/email
      userName: userData.userName,
      displayName: userData.displayName,
      roles: activeRoles.map((role: any) => {
        if (typeof role === 'string') {
          return role; // Keep role codes as strings
        } else {
          // Return full role object with proper structure
          return {
            id: role.id,
            roleCode: role.roleCode,
            roleName: role.roleName || role.roleCode,
            description: role.description,
            classification: role.classification,
            scopeKind: role.scopeKind,
            principalKind: role.principalKind,
            status: role.status
          } as UserRole;
        }
      }),
      logins: activeLogins,
      systemPermissions: [], // Will be populated below
      // Login tracking fields
      lastLoginAt: userData.lastLoginAt,
      previousLoginAt: userData.previousLoginAt,
      lastLoginProvider: userData.lastLoginProvider,
      loginCount: userData.loginCount || 0
    };

    logger.auth('Complete user object created from API data', {
      userId: user.id,
      userIdAsNumber: Number(user.id),
      email: user.email,
      name: user.name,
      userName: user.userName,
      displayName: user.displayName,
      rolesCount: user.roles?.length || 0,
      loginsCount: activeLogins.length,
      roleDetails: activeRoles.map((r: any) => typeof r === 'string' ? r : ({ name: r.roleName, code: r.roleCode })),
      loginDetails: activeLogins.map((l: any) => ({ provider: l.loginProvider, name: l.loginName })),
      hasCompleteData: !!completeUserData
    });

    // Load user permissions from API
    try {
      logger.auth('Loading user permissions from API', { userId: numericUserId });
      const permissionsResponse = await apiClient.get(
        `${apiConstants.API_USERS_ME_PERMISSIONS}`,
        {
          params: {
            partitionKey: "sys:smart_matrix"
          }
        }
      );

      if (permissionsResponse.data.succeeded && permissionsResponse.data.data?.permissions) {
        user.systemPermissions = permissionsResponse.data.data.permissions;
        logger.auth('User permissions loaded successfully', { 
          permissionsCount: user.systemPermissions.length,
          permissions: user.systemPermissions
        });
      } else {
        logger.warn('No permissions returned from API, user will have empty permissions');
      }
    } catch (permError) {
      logger.error('Failed to load user permissions, continuing with empty permissions', permError);
      // Don't fail login if permissions can't be loaded
    }

    // Step 5: Retrieve user preferences from API
    logger.auth('Loading user preferences from API', { userId: numericUserId });
    const preferences: UserPreferences = {
      language: 'en-US',
      theme: 'system',
      colorTheme: 'purple',
      timezone: 'UTC',
      dateFormat: 'relative',
      emailNotifications: true,
      pushNotifications: true
    };

    try {
      // Load language preference
      const languageResponse = await apiClient.get(
        `${apiConstants.API_PREFERENCES_LANGUAGE_GET}`,
        {
          params: {
            userId: numericUserId
          }
        }
      );

      if (languageResponse.data.succeeded && languageResponse.data.data?.preference?.languageCode) {
        preferences.language = languageResponse.data.data.preference.languageCode;
        logger.auth('Language preference loaded successfully', { language: preferences.language });
      } else {
        logger.auth('No language preference found, using default', { 
          userId: numericUserId,
          responseSucceeded: languageResponse.data.succeeded,
          hasData: !!languageResponse.data.data,
          hasPreference: !!languageResponse.data.data?.preference,
          hasLanguageCode: !!languageResponse.data.data?.preference?.languageCode
        });
      }

      // Load theme preference
      const themeResponse = await apiClient.get(
        `${apiConstants.API_PREFERENCES_THEME}`,
        {
          params: {
            userId: numericUserId
          }
        }
      );

      if (themeResponse.data.succeeded && themeResponse.data.data?.preference) {
        const themeData = themeResponse.data.data.preference;
        if (themeData.themePreference) {
          preferences.theme = themeData.themePreference;
        }
        if (themeData.colorThemePreference) {
          preferences.colorTheme = themeData.colorThemePreference;
        }
        logger.auth('Theme preferences loaded successfully', { 
          theme: preferences.theme, 
          colorTheme: preferences.colorTheme 
        });
      } else {
        logger.auth('No theme preferences found, using defaults');
      }

    } catch (prefError) {
      logger.error('Failed to load user preferences during login, using defaults', prefError);
      // Continue with default preferences - don't fail login
    }

    const tenants: Tenant[] = [{
      id: 'default',
      name: 'Default Tenant',
      domain: 'default',
      isDefault: true
    }];

    const loginData: LoginSuccessPayload = {
      user,
      tenants,
      currentTenant: tenants[0],
      permissions: []
    };

    // Debug logging before Redux dispatch
    logger.auth('About to dispatch to Redux', {
      loginData,
      userObject: user,
      hasUser: !!user,
      userId: user?.id,
      userName: user?.name,
      userEmail: user?.email,
      context
    });

    console.log('🔥 CRITICAL DEBUG - About to dispatch to Redux:', {
      context,
      actionType: context === 'accountSwitch' ? 'accountSwitchSuccess' : context === 'login' ? 'loginSuccess' : 'initializationSuccess',
      hasUser: !!user,
      userId: user?.id,
      userName: user?.name,
      userEmail: user?.email,
      reduxStoreExists: !!store,
      dispatchFunction: typeof store.dispatch,
      timestamp: new Date().toISOString()
    });

    // Update Redux state based on context
    console.log('🎯 [authManager] About to dispatch Redux action', {
      context,
      hasUser: !!user,
      userId: user?.id,
      userName: user?.name,
      timestamp: new Date().toISOString()
    });
    
    try {
      if (context === 'accountSwitch') {
        console.log('📤 [authManager] Dispatching accountSwitchSuccess', {
          timestamp: new Date().toISOString()
        });
        store.dispatch(accountSwitchSuccess(loginData));
        console.log('✅ [authManager] accountSwitchSuccess dispatched', {
          timestamp: new Date().toISOString()
        });
        logger.auth('User info retrieved and stored in Redux successfully for account switch');
      } else if (context === 'login') {
        console.log('📤 [authManager] Dispatching loginSuccess', {
          timestamp: new Date().toISOString()
        });
        store.dispatch(loginSuccess(loginData));
        console.log('✅ [authManager] loginSuccess dispatched', {
          timestamp: new Date().toISOString()
        });
        logger.auth('User info retrieved and stored in Redux successfully for login');
      } else if (context === 'initialization') {
        // This is initialization context
        console.log('📤 [authManager] Dispatching initializationSuccess for initialization context', {
          timestamp: new Date().toISOString(),
          loginData: {
            hasUser: !!loginData.user,
            userId: loginData.user?.id,
            userName: loginData.user?.name
          }
        });
        store.dispatch(initializationSuccess(loginData));
        console.log('✅ [authManager] initializationSuccess dispatched successfully', {
          timestamp: new Date().toISOString()
        });
        logger.auth('User info retrieved and stored in Redux successfully for initialization');
      } else {
        // Fallback to login for backward compatibility
        console.log('⚠️ [authManager] Unknown context, dispatching loginSuccess (fallback)', {
          context,
          timestamp: new Date().toISOString()
        });
        store.dispatch(loginSuccess(loginData));
        console.log('✅ [authManager] loginSuccess dispatched (fallback)', {
          timestamp: new Date().toISOString()
        });
        logger.auth('User info retrieved and stored in Redux successfully (fallback to login)');
      }
      
      console.log('🎉 [authManager] Redux dispatch completed successfully', {
        context,
        timestamp: new Date().toISOString()
      });
    } catch (dispatchError) {
      console.error('❌ [authManager] Redux dispatch FAILED', {
        error: dispatchError,
        message: dispatchError instanceof Error ? dispatchError.message : String(dispatchError),
        stack: dispatchError instanceof Error ? dispatchError.stack : undefined,
        context,
        timestamp: new Date().toISOString()
      });
      throw dispatchError;
    }

    // Verify Redux state was updated
    setTimeout(() => {
      const newState = store.getState();
      console.log('🔥 CRITICAL DEBUG - Redux state after dispatch:', {
        isAuthenticated: newState.auth.isAuthenticated,
        hasUser: !!newState.auth.user,
        userId: newState.auth.user?.id,
        userName: newState.auth.user?.name,
        userEmail: newState.auth.user?.email,
        fullAuthState: newState.auth
      });
    }, 100);

    // Permissions are already stored in user.systemPermissions from API
    // Dispatch them to Redux to ensure they're available throughout the app
    try {
      logger.auth('Dispatching user permissions to Redux from API', {
        permissionCount: user.systemPermissions?.length || 0,
        permissions: user.systemPermissions || [],
        source: 'API /me/permissions endpoint'
      });
      
      const permissions: string[] = (user.systemPermissions || []).map(p => {
        if (typeof p === 'string') {
          return p;
        } else if (p && typeof p === 'object' && 'resource' in p) {
          // Permission object with resource and actions
          return p.resource;
        } else {
          // Fallback for any other structure
          return String(p);
        }
      });
      const roles = user.roles || [];
      
      // Dispatch permissions to Redux
      const { setUserPermissions } = await import('../../store/slices/authSlice');
      store.dispatch(setUserPermissions({ 
        permissions, 
        roles: roles.map((r: any) => typeof r === 'string' ? r : r.roleCode) 
      }));
      
      logger.auth('✅ Permissions dispatched to Redux successfully', { 
        permissionCount: permissions.length,
        permissions,
        roles: roles.map((r: any) => typeof r === 'string' ? r : r.roleCode)
      });
    } catch (permissionError) {
      logger.error('❌ Failed to dispatch permissions to Redux', permissionError);
    }

    // Clean up the token-specific mutex on success
    if (typeof window !== 'undefined') {
      const tokenHash = accessToken.substring(0, 20);
      const userInfoKey = `user_info_${tokenHash}_${context}`;
      sessionStorage.removeItem(userInfoKey);
    }

    // Apply preferences to Redux store and DOM immediately after login
    await this.applyUserPreferencesToUI(preferences, Number(user.id));
  } catch (error) {
    logger.error('Failed to retrieve and store user info', error);
    throw error; // Re-throw to maintain error handling behavior
  }
}

  /**
   * Apply user preferences to Redux store and DOM
   */
  private async applyUserPreferencesToUI(preferences: UserPreferences, userId: number): Promise<void> {
    try {
      logger.auth('Applying user preferences to UI', { preferences, userId });

      // Fetch ALL user preferences from backend (including notification settings)
      // This ensures we have the complete preferences, not just language and theme
      try {
        console.log('🔥 [AuthManager] About to fetch user preferences', { userId });
        const { fetchUserPreferences } = await import('../../store/slices/userPreferencesSlice');
        console.log('🔥 [AuthManager] fetchUserPreferences imported, dispatching...');
        const result = await store.dispatch(fetchUserPreferences(userId)).unwrap();
        console.log('🔥 [AuthManager] fetchUserPreferences completed successfully', { result });
        logger.auth('All user preferences fetched from backend successfully', { 
          fetchedPreferences: result,
          notificationSettings: {
            systemEnabled: result.notificationSystemEnabled,
            toolEnabled: result.notificationToolEnabled,
            adminEnabled: result.notificationAdminEnabled,
            securityEnabled: result.notificationSecurityEnabled,
            toastEnabled: result.notificationToastEnabled,
            position: result.notificationToastPosition,
            duration: result.notificationToastDuration
          }
        });
        
        // Verify Redux state was updated
        const state = store.getState();
        console.log('🔔 [AuthManager] Notification preferences loaded on login:', {
          fromAPI: result,
          inRedux: state.userPreferences.preferences,
          notificationSettings: {
            systemEnabled: state.userPreferences.preferences?.notificationSystemEnabled,
            toolEnabled: state.userPreferences.preferences?.notificationToolEnabled,
            adminEnabled: state.userPreferences.preferences?.notificationAdminEnabled,
            securityEnabled: state.userPreferences.preferences?.notificationSecurityEnabled,
            toastEnabled: state.userPreferences.preferences?.notificationToastEnabled,
            position: state.userPreferences.preferences?.notificationToastPosition,
            duration: state.userPreferences.preferences?.notificationToastDuration
          }
        });
      } catch (fetchError) {
        console.error('🔥 [AuthManager] Failed to fetch user preferences:', fetchError);
        logger.error('Failed to fetch all user preferences, continuing with partial preferences', fetchError);
      }

      // Apply language preference to Redux store (direct dispatch, no API call)
      if (preferences.language) {
        logger.auth('Applying language preference to Redux', { language: preferences.language });
        const { setLanguage } = await import('../../store/slices/languageSlice');
        store.dispatch(setLanguage(preferences.language as any));
      }

      // Apply theme preferences to Redux store and DOM (direct dispatch, no API call)
      if (preferences.theme || preferences.colorTheme) {
        logger.auth('Applying theme preferences to Redux and DOM', { 
          theme: preferences.theme, 
          colorTheme: preferences.colorTheme 
        });
        
        const { setThemeMode, setColorTheme } = await import('../../store/slices/themeSlice');
        const { updateHtmlAttributes } = await import('../../features/theme/utils/simpleThemeUtils');
        
        if (preferences.theme) {
          store.dispatch(setThemeMode(preferences.theme as any));
        }
        if (preferences.colorTheme) {
          store.dispatch(setColorTheme(preferences.colorTheme as any));
        }
        
        // Apply theme to DOM immediately
        updateHtmlAttributes(
          preferences.theme as any || 'system', 
          preferences.colorTheme as any || 'purple'
        );
      }

      logger.auth('User preferences applied to UI successfully');
    } catch (error) {
      logger.error('Failed to apply user preferences to UI', error);
      // Don't throw - login should still succeed even if preference application fails
    }
  }

  /**
   * Store the current account in the multi-account system after login
   * Returns the account ID if successful, null otherwise
   */
  private async storeCurrentAccountInMultiAccountSystem(credentials: LoginCredentials, tokenData: any): Promise<string | null> {
    try {
      logger.auth('Storing current account in multi-account system', { loginName: credentials.loginName });
      
      // Get the current user data from Redux state
      const state = store.getState();
      const currentUser = state.auth.user;
      
      if (!currentUser) {
        logger.warn('No current user found, cannot store in multi-account system');
        return null;
      }
      
      // Import account storage
      const { accountStorage, AccountCredentialsHelper } = await import('../../features/auth/utils/accountStorage');
      
      // Check if this account already exists
      const isDuplicate = await AccountCredentialsHelper.isDuplicateAccount(
        currentUser.email, 
        accountStorage, 
        credentials.loginName
      );
      
      if (isDuplicate) {
        logger.auth('Account already exists in multi-account system, updating');
        const existingAccount = await AccountCredentialsHelper.findExistingAccount(
          currentUser.email, 
          accountStorage, 
          credentials.loginName
        );
        
        if (existingAccount) {
          await accountStorage.updateLastUsed(existingAccount.id);
          accountStorage.setActiveAccountId(existingAccount.id);
          
          // Set the account context in tokenManager
          tokenManager.setAccountContext(existingAccount.id);
          
          logger.auth('Existing account updated and set as active');
          return existingAccount.id;
        }
      }
      
      // Create auth secret for the current account
      const authSecret = {
        loginName: credentials.loginName,
        userName: currentUser.name,
        token: {
          accessToken: tokenData.accessToken,
          accessToken_LifeInMinutes: 60,
          accessToken_Expires: new Date(Date.now() + (60 * 60 * 1000)).toISOString(),
          refreshToken: tokenData.refreshToken || '',
          refreshToken_LifeInMinutes: 1440,
          refreshToken_Expires: new Date(Date.now() + (24 * 60 * 60 * 1000)).toISOString()
        }
      };
      
      // Default persistence options
      const persistenceOptions = {
        rememberMe: true,
        persistenceDays: 30
      };
      
      // Create and save the account
      const storedAccount = await AccountCredentialsHelper.createStoredAccount(
        authSecret,
        persistenceOptions,
        currentUser.email
      );
      
      await accountStorage.saveAccount(storedAccount);
      accountStorage.setActiveAccountId(storedAccount.id);
      
      // Set the account context in tokenManager for multi-account token storage
      tokenManager.setAccountContext(storedAccount.id);
      
      logger.auth('Current account stored in multi-account system', { 
        accountId: storedAccount.id,
        loginName: storedAccount.loginName 
      });
      
      return storedAccount.id;
      
    } catch (error) {
      logger.error('Failed to store current account in multi-account system', error);
      // Don't throw error - login should still succeed even if multi-account storage fails
      return null;
    }
  }

  /**
   * Store the current Google account in the multi-account system after login
   * Returns the account ID if successful, null otherwise
   */
  private async storeCurrentAccountInMultiAccountSystemForGoogle(token: string): Promise<string | null> {
    try {
      logger.auth('Storing current Google account in multi-account system');
      
      // Get the current user data from Redux state with retry logic
      let currentUser = null;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (!currentUser && retryCount < maxRetries) {
        const state = store.getState();
        currentUser = state.auth.user;
        
        if (!currentUser) {
          logger.warn(`No current user found in Redux state (attempt ${retryCount + 1}/${maxRetries}), retrying...`);
          retryCount++;
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 200)); // Wait 200ms before retry
          }
        }
      }
      
      if (!currentUser) {
        logger.error('No current user found after multiple retries, cannot store Google account in multi-account system');
        return null;
      }
      
      logger.auth('Current user found in Redux state', { 
        email: currentUser.email, 
        name: currentUser.name,
        id: currentUser.id 
      });
      
      // For Google login, we use the email as the login name
      const loginName = currentUser.email;
      
      // Import account storage
      const { accountStorage, AccountCredentialsHelper } = await import('../../features/auth/utils/accountStorage');
      
      // Check if this account already exists
      const isDuplicate = await AccountCredentialsHelper.isDuplicateAccount(
        currentUser.email, 
        accountStorage, 
        loginName
      );
      
      if (isDuplicate) {
        logger.auth('Google account already exists in multi-account system, updating');
        const existingAccount = await AccountCredentialsHelper.findExistingAccount(
          currentUser.email, 
          accountStorage, 
          loginName
        );
        
        if (existingAccount) {
          await accountStorage.updateLastUsed(existingAccount.id);
          accountStorage.setActiveAccountId(existingAccount.id);
          
          // Set the account context in tokenManager
          tokenManager.setAccountContext(existingAccount.id);
          
          logger.auth('Existing Google account updated and set as active');
          return existingAccount.id;
        }
      }
      
      // Create auth secret for the current Google account
      const authSecret = {
        loginName: loginName,
        userName: currentUser.name,
        token: {
          accessToken: token,
          accessToken_LifeInMinutes: 60,
          accessToken_Expires: new Date(Date.now() + (60 * 60 * 1000)).toISOString(),
          refreshToken: '', // Google OAuth might not provide refresh token
          refreshToken_LifeInMinutes: 1440,
          refreshToken_Expires: new Date(Date.now() + (24 * 60 * 60 * 1000)).toISOString()
        }
      };
      
      // Default persistence options
      const persistenceOptions = {
        rememberMe: true,
        persistenceDays: 30
      };
      
      // Create and save the account
      const storedAccount = await AccountCredentialsHelper.createStoredAccount(
        authSecret,
        persistenceOptions,
        currentUser.email
      );
      
      await accountStorage.saveAccount(storedAccount);
      accountStorage.setActiveAccountId(storedAccount.id);
      
      // Set the account context in tokenManager for multi-account token storage
      tokenManager.setAccountContext(storedAccount.id);
      
      logger.auth('Current Google account stored in multi-account system', { 
        accountId: storedAccount.id,
        loginName: storedAccount.loginName 
      });
      
      return storedAccount.id;
      
    } catch (error) {
      logger.error('Failed to store current Google account in multi-account system', error);
      // Don't throw error - login should still succeed even if multi-account storage fails
      return null;
    }
  }

  /**
   * Login with credentials - SIMPLIFIED
   */
  async login(credentials: LoginCredentials): Promise<AuthResult> {
    // Prevent race conditions
    if (this.loginMutex) {
      return { success: false, error: 'Login already in progress' };
    }

    this.loginMutex = true;
    
    try {
      logger.auth('Starting login process', { loginName: credentials.loginName });
      
      // Update Redux state - start login
      store.dispatch(startLogin());

      // Make API call
      const axios = (await import('axios')).default;
      const apiConstants = await import('../../constants/apiConstants');

      // Read cached public IP from localStorage (populated by apiClient.ts on page load)
      let clientIp: string | null = null;
      try { clientIp = localStorage.getItem('sm_public_ip'); } catch { /* ignore */ }

      const response = await axios.post(
        `${apiConstants.API_BASE_URL}${apiConstants.API_AUTH_STANDARD_LOGIN}`,
        {
          partitionKey: "sys:smart_matrix",
          loginName: credentials.loginName,
          password: credentials.password,
          returnUrl: window.location.origin + '/main'
        },
        clientIp ? { headers: { 'X-Client-IP': clientIp } } : undefined
      );

      const result = response.data;
      
      if (result.succeeded && result.data) {
        // Store tokens using tokenManager (with verification)
        const tokenPair: TokenPair = {
          accessToken: result.data.token.accessToken,
          refreshToken: result.data.token.refreshToken,
          expiresAt: Date.now() + (result.data.token.accessToken_LifeInMinutes * 60 * 1000),
          tokenType: 'Bearer'
        };

        logger.auth('Storing tokens via tokenManager', {
          expiresIn: result.data.token.accessToken_LifeInMinutes + ' minutes'
        });

        // IMPORTANT: Clear any existing account context first
        // This prevents storing the new token with the wrong account ID
        tokenManager.setAccountContext(null);

        tokenManager.storeTokens(tokenPair);

        // VERIFY token is retrievable before proceeding - critical for preventing race conditions
        const storedTokens = tokenManager.getTokens();
        if (!storedTokens || storedTokens.accessToken !== tokenPair.accessToken) {
          logger.error('Token storage verification failed after login', {
            stored: !!storedTokens,
            matches: storedTokens?.accessToken === tokenPair.accessToken
          });
          throw new Error('Token storage verification failed');
        }

        logger.auth('Token storage verified, proceeding with user info retrieval');

        // Schedule automatic token refresh (must be done after storage verification)
        tokenManager.scheduleTokenRefresh();
        logger.auth('Token refresh scheduled');

        // Update login time to prevent immediate auto-logout from apiProxy
        try {
          const { updateLastLoginTime } = await import('../../features/shared/utils/apiProxy');
          updateLastLoginTime();
        } catch (error) {
          logger.warn('Failed to update login time for apiProxy', error);
        }

        // Use shared function to retrieve user info and store in Redux
        await this.retrieveAndStoreUserInfo(result.data.token.accessToken);
        
        // Store this account in the multi-account system and get the account ID
        const accountId = await this.storeCurrentAccountInMultiAccountSystem(credentials, result.data.token);
        
        // Now that we have an account ID, update the token storage with account context
        if (accountId) {
          logger.auth('Updating token storage with account context', { accountId });
          tokenManager.setAccountContext(accountId);
          // Re-store tokens with the account context
          tokenManager.storeTokens(tokenPair);
          
          // Update Redux state with the activeAccountId
          const { setActiveAccount } = await import('../../store/slices/authSlice');
          store.dispatch(setActiveAccount(accountId));
        }
        
        logger.auth('Standard login completed successfully');
        return { success: true };
      } else {
        const error = `Login failed: ${result.messages?.join(', ') || 'Invalid credentials'}`;
        store.dispatch(loginFailure({
          type: AuthErrorType.InvalidCredentials,
          message: error,
          userMessage: 'Invalid username or password. Please try again.',
          code: 'LOGIN_FAILED',
          recoverable: false,
          retryable: true,
          timestamp: Date.now()
        }));
        
        return { success: false, error: 'Invalid username or password. Please try again.' };
      }
    } catch (error) {
      logger.error('Login failed', error);

      // Check if this is actually a business-level failure (succeeded: false) that came
      // back as an Axios error due to a response interceptor or non-2xx status code.
      const axiosResp = (error as any)?.response;
      const responseData = axiosResp?.data;
      if (responseData && responseData.succeeded === false) {
        // Distinguish infrastructure failures (503 / statusCode -3) from bad credentials
        // so the user sees "service temporarily unavailable" instead of "invalid password".
        const isServiceUnavailable =
          axiosResp?.status === 503 || responseData.statusCode === -3;

        if (isServiceUnavailable) {
          const serverMessage =
            responseData.messages?.join(', ') ||
            'Service temporarily unavailable. Please try again in a moment.';
          store.dispatch(loginFailure({
            type: AuthErrorType.ServerError,
            message: serverMessage,
            userMessage: serverMessage,
            code: 'AUTH_503',
            recoverable: false,
            retryable: true,
            timestamp: Date.now()
          }));
          return { success: false, error: serverMessage };
        }

        const businessError = `Login failed: ${responseData.messages?.join(', ') || 'Invalid credentials'}`;
        store.dispatch(loginFailure({
          type: AuthErrorType.InvalidCredentials,
          message: businessError,
          userMessage: 'Invalid username or password. Please try again.',
          code: 'LOGIN_FAILED',
          recoverable: false,
          retryable: true,
          timestamp: Date.now()
        }));
        return { success: false, error: 'Invalid username or password. Please try again.' };
      }

      // No response at all — genuine network/SSL failure
      const axiosError = error as any;
      const isNetworkError = !axiosError?.response && (
        axiosError?.code === 'ERR_NETWORK' ||
        axiosError?.message === 'Network Error' ||
        axiosError?.isNetworkError === true
      );
      const errorMessage = isNetworkError
        ? 'Cannot connect to the server. If using HTTPS locally, please open the API URL in your browser and accept the certificate, then try again.'
        : 'Login failed. Please try again.';

      store.dispatch(loginFailure({
        type: AuthErrorType.NetworkUnavailable,
        message: error instanceof Error ? error.message : 'Network error',
        userMessage: errorMessage,
        code: 'LOGIN_NETWORK_ERROR',
        recoverable: false,
        retryable: true,
        timestamp: Date.now()
      }));
      
      return { success: false, error: errorMessage };
    } finally {
      this.loginMutex = false;
    }
  }

  /**
   * Login with Google OAuth token - FETCH REAL USER DATA
   */
  async loginWithGoogle(token: string): Promise<AuthResult> {
    // Prevent race conditions
    if (this.loginMutex) {
      logger.auth('Google login already in progress, rejecting duplicate request');
      return { success: false, error: 'Login already in progress' };
    }

    this.loginMutex = true;
    
    try {
      logger.auth('Starting Google login process');
      
      store.dispatch(startLogin());

      // Note: For Google login, we receive a JWT token directly
      // We need to extract expiration from the token or use a default
      // Store token using tokenManager
      logger.auth('Storing Google OAuth token via tokenManager');
      
      // Parse JWT to get expiration
      let expiresAt = Date.now() + (60 * 60 * 1000); // Default: 1 hour
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          if (payload.exp) {
            expiresAt = payload.exp * 1000; // Convert to milliseconds
          }
        }
      } catch (error) {
        logger.warn('Failed to parse JWT expiration, using default', error);
      }

      const tokenPair: TokenPair = {
        accessToken: token,
        refreshToken: '', // Google OAuth may not provide refresh token initially
        expiresAt,
        tokenType: 'Bearer'
      };

      logger.auth('Storing Google OAuth token via tokenManager');
      tokenManager.storeTokens(tokenPair);

      // VERIFY token is retrievable before proceeding
      const storedTokens = tokenManager.getTokens();
      if (!storedTokens || storedTokens.accessToken !== token) {
        logger.error('Token storage verification failed after Google login', {
          stored: !!storedTokens,
          matches: storedTokens?.accessToken === token
        });
        throw new Error('Token storage verification failed');
      }

      logger.auth('Google token storage verified, proceeding with user info retrieval');

      // Schedule automatic token refresh (must be done after storage verification)
      tokenManager.scheduleTokenRefresh();
      logger.auth('Token refresh scheduled for Google token');

      // Update login time to prevent immediate auto-logout from apiProxy
      try {
        const { updateLastLoginTime } = await import('../../features/shared/utils/apiProxy');
        updateLastLoginTime();
      } catch (error) {
        logger.warn('Failed to update login time for apiProxy', error);
      }

      // Use shared function to retrieve user info and store in Redux
      await this.retrieveAndStoreUserInfo(token, 'login');
      
      // Wait for Redux state to be properly set before proceeding with multi-account operations
      logger.auth('Waiting for authentication state to be confirmed before multi-account storage');
      const authStateConfirmed = await this.waitForAuthenticationState(5000); // Wait up to 5 seconds
      
      if (authStateConfirmed) {
        logger.auth('Authentication state confirmed, proceeding with multi-account storage');
        // Now that Redux state is confirmed, store this account in the multi-account system
        const accountId = await this.storeCurrentAccountInMultiAccountSystemForGoogle(token);
        
        // Update token storage with account context
        if (accountId) {
          logger.auth('Updating Google token storage with account context', { accountId });
          tokenManager.setAccountContext(accountId);
          // Re-store tokens with the account context
          tokenManager.storeTokens(tokenPair);
          
          // Update Redux state with the activeAccountId
          const { setActiveAccount } = await import('../../store/slices/authSlice');
          store.dispatch(setActiveAccount(accountId));
        }
      } else {
        logger.warn('Authentication state not confirmed, skipping multi-account storage');
      }
      
      if (authStateConfirmed) {
        logger.auth('Google login completed successfully with confirmed auth state');
        return { success: true };
      } else {
        logger.warn('Google login completed but auth state not confirmed, proceeding anyway');
        return { success: true }; // Still return success to avoid blocking user
      }
    } catch (error) {
      logger.error('Google login failed', error);
      
      store.dispatch(loginFailure({
        type: AuthErrorType.OAuthError,
        message: error instanceof Error ? error.message : 'Google login error',
        userMessage: 'Google login failed. Please try again.',
        code: 'GOOGLE_LOGIN_FAILED',
        recoverable: false,
        retryable: true,
        timestamp: Date.now()
      }));
      
      return { success: false, error: 'Google login failed. Please try again.' };
    } finally {
      this.loginMutex = false;
    }
  }

  /**
   * Logout - SIMPLIFIED
   */
  async logout(): Promise<AuthResult> {
    try {
      logger.auth('Starting logout process');
      
      store.dispatch(startLogout());

      // Clear tokens using tokenManager (handles all token storage keys)
      tokenManager.clearTokens();

      // Clear account storage
      try {
        const { accountStorage } = await import('../../features/auth/utils/accountStorage');
        await accountStorage.clearAllAccounts();
      } catch (error) {
        logger.warn('Failed to clear account storage', error);
      }

      store.dispatch(logoutSuccess());
      
      logger.auth('Logout completed successfully');
      return { success: true };
      
    } catch (error) {
      logger.error('Logout failed', error);
      
      // Force logout even on error - ensure tokens are cleared
      tokenManager.clearTokens();
      
      store.dispatch(logoutFailure({
        type: AuthErrorType.WorkflowInterrupted,
        message: error instanceof Error ? error.message : 'Logout error',
        userMessage: 'Logout encountered an error, but you have been signed out.',
        code: 'LOGOUT_ERROR',
        recoverable: false,
        retryable: false,
        timestamp: Date.now()
      }));
      
      return { success: false, error: 'Logout encountered an error, but you have been signed out.' };
    }
  }

  /**
   * Initialize session from stored data - FETCH REAL USER DATA
   */
  async initializeSession(): Promise<AuthResult> {
    try {
      console.log('🚀 [authManager] initializeSession - START', {
        timestamp: new Date().toISOString()
      });
      logger.auth('Starting session initialization');
      
      console.log('📤 [authManager] Dispatching initializeFromStorage', {
        timestamp: new Date().toISOString()
      });
      store.dispatch(initializeFromStorage());

      // Check for stored tokens using tokenManager
      const tokens = tokenManager.getTokens();
      const accessToken = tokens?.accessToken || null;

      logger.auth('Session initialization - token check', {
        hasAccessToken: !!accessToken,
        tokenLength: accessToken?.length || 0,
        tokenPreview: accessToken ? `${accessToken.substring(0, 20)}...` : null,
        tokenValid: tokens ? tokenManager.isAccessTokenValid() : false,
        tokenExpiry: tokens?.expiresAt ? new Date(tokens.expiresAt).toISOString() : null,
        windowAvailable: typeof window !== 'undefined'
      });

      console.log('AuthManager: Session initialization - token check', {
        hasAccessToken: !!accessToken,
        tokenLength: accessToken?.length || 0,
        tokenPreview: accessToken ? `${accessToken.substring(0, 20)}...` : null,
        tokenValid: tokens ? tokenManager.isAccessTokenValid() : false,
        tokenExpiry: tokens?.expiresAt ? new Date(tokens.expiresAt).toISOString() : null,
        windowAvailable: typeof window !== 'undefined'
      });

      if (!accessToken) {
        store.dispatch(initializationFailure({
          type: AuthErrorType.TokenInvalid,
          message: 'No valid tokens found',
          userMessage: 'Please log in to continue.',
          code: 'NO_VALID_TOKENS',
          recoverable: false,
          retryable: false,
          timestamp: Date.now()
        }));
        return { success: false, error: 'No valid session found' };
      }

      // Fetch real user data from API using the stored token
      await import('../../constants/apiConstants');
      
      try {
        logger.auth('Session initialization - using shared user info retrieval');
        
        // Update login time to prevent immediate auto-logout from apiProxy
        try {
          const { updateLastLoginTime } = await import('../../features/shared/utils/apiProxy');
          updateLastLoginTime();
        } catch (error) {
          logger.warn('Failed to update login time for apiProxy', error);
        }
        
        // Use shared function to retrieve user info and store in Redux
        console.log('📡 [authManager] Calling retrieveAndStoreUserInfo with initialization context', {
          timestamp: new Date().toISOString(),
          accessTokenPreview: accessToken ? `${accessToken.substring(0, 20)}...` : null
        });
        
        try {
          await this.retrieveAndStoreUserInfo(accessToken, 'initialization');
          console.log('✅ [authManager] retrieveAndStoreUserInfo completed successfully', {
            timestamp: new Date().toISOString()
          });
          
          // Verify Redux state was updated
          const finalState = store.getState();
          console.log('🔍 [authManager] Final Redux state after retrieveAndStoreUserInfo', {
            isAuthenticated: finalState.auth.isAuthenticated,
            isLoading: finalState.auth.isLoading,
            isInitialized: finalState.auth.isInitialized,
            hasUser: !!finalState.auth.user,
            userName: finalState.auth.user?.name,
            timestamp: new Date().toISOString()
          });
        } catch (retrieveError) {
          console.error('❌ [authManager] retrieveAndStoreUserInfo FAILED', {
            error: retrieveError,
            message: retrieveError instanceof Error ? retrieveError.message : String(retrieveError),
            stack: retrieveError instanceof Error ? retrieveError.stack : undefined,
            timestamp: new Date().toISOString()
          });
          throw retrieveError;
        }
        
        console.log('✅ [authManager] initializeSession - SUCCESS', {
          timestamp: new Date().toISOString()
        });
        logger.auth('Session initialization completed successfully with real user data');
        return { success: true };
      } catch (apiError) {
        logger.error('Failed to retrieve user data during initialization', apiError, {
          errorStatus: (apiError as any)?.response?.status,
          errorData: (apiError as any)?.response?.data,
          errorMessage: (apiError as any)?.message,
          hasToken: !!accessToken,
          tokenPreview: accessToken ? `${accessToken.substring(0, 20)}...` : null
        });
        
        // Check if this is a token refresh failure (401 error)
        const isTokenRefreshFailure = (apiError as any)?.statusCode === 401 || 
                                      (apiError as any)?.response?.status === 401;
        
        if (isTokenRefreshFailure) {
          logger.auth('Token refresh failed during initialization, performing cleanup and redirect');
        }
        
        // Clear the tokens using tokenManager
        tokenManager.clearTokens();
        logger.auth('Tokens cleared after initialization failure');
        
        // Dispatch failure to Redux
        store.dispatch(initializationFailure({
          type: AuthErrorType.TokenInvalid,
          message: 'Token validation failed',
          userMessage: 'Your session has expired. Please log in again.',
          code: 'TOKEN_VALIDATION_FAILED',
          recoverable: false,
          retryable: false,
          timestamp: Date.now()
        }));
        logger.auth('Initialization failure dispatched to Redux');
        
        // Do NOT redirect to login here — let page-level auth guards decide.
        // Public pages (e.g. index "/") should remain accessible even when
        // a stored session has expired.
        logger.auth('Session expired — cleanup complete, page-level guards will redirect if needed');

        return { success: false, error: 'Your session has expired. Please log in again.' };
      }

    } catch (error) {
      logger.error('Session initialization failed', error);

      // Clear tokens on any initialization error
      tokenManager.clearTokens();
      logger.auth('Tokens cleared after initialization error');

      store.dispatch(initializationFailure({
        type: AuthErrorType.WorkflowInterrupted,
        message: error instanceof Error ? error.message : 'Initialization error',
        userMessage: 'Failed to restore your session. Please log in again.',
        code: 'INITIALIZATION_FAILED',
        recoverable: false,
        retryable: true,
        timestamp: Date.now()
      }));
      logger.auth('Initialization failure dispatched to Redux');

      // Do NOT redirect to login here — let page-level auth guards decide.
      logger.auth('Initialization failed — cleanup complete, page-level guards will redirect if needed');

      return { success: false, error: 'Failed to restore your session. Please log in again.' };
    }
  }

  // Multi-account methods - SIMPLIFIED STUBS (implement later)
  async getAvailableAccounts(): Promise<StoredAccount[]> {
    try {
      const { accountStorage } = await import('../../features/auth/utils/accountStorage');
      return await accountStorage.getAllAccounts();
    } catch (error) {
      logger.error('Failed to get available accounts', error);
      return [];
    }
  }

  async switchAccount(accountId: string): Promise<AuthResult> {
    try {
      logger.auth('Starting account switch process', { accountId });
      
      // Update Redux state - start account switch
      store.dispatch(startAccountSwitchWorkflow(accountId));

      // Get the account from storage
      const { accountStorage, AccountCredentialsHelper } = await import('../../features/auth/utils/accountStorage');
      const account = await accountStorage.getAccount(accountId);
      
      console.log('🔥 [authManager.switchAccount] Retrieved account from storage:', {
        requestedAccountId: accountId,
        retrievedAccountId: account?.id,
        retrievedLoginName: account?.loginName,
        retrievedUserName: account?.userName,
        accountMatch: account?.id === accountId,
        account: account
      });
      
      if (!account) {
        const error = `Account ${accountId} not found in storage`;
        logger.error(error);
        store.dispatch(accountSwitchFailure({
          type: AuthErrorType.InvalidCredentials,
          message: error,
          userMessage: 'Account not found. Please log in again.',
          code: 'ACCOUNT_NOT_FOUND',
          recoverable: false,
          retryable: false,
          timestamp: Date.now()
        }));
        return { success: false, error: 'Account not found. Please log in again.' };
      }

      // Extract auth secret from stored account
      let authSecret;
      try {
        authSecret = await AccountCredentialsHelper.extractAuthSecret(account);
      } catch (extractError) {
        logger.error('Failed to extract auth secret during account switch', extractError);
        store.dispatch(accountSwitchFailure({
          type: AuthErrorType.TokenInvalid,
          message: extractError instanceof Error ? extractError.message : 'Failed to decrypt account',
          userMessage: 'Account credentials are corrupted. Please log in again.',
          code: 'ACCOUNT_DECRYPT_FAILED',
          recoverable: false,
          retryable: false,
          timestamp: Date.now()
        }));
        return { success: false, error: 'Account credentials are corrupted. Please log in again.' };
      }

      // Validate token expiration before using it
      const now = new Date();
      const tokenExpiry = new Date(authSecret.token.accessToken_Expires);
      
      if (tokenExpiry <= now) {
        logger.warn('Stored token has expired for account', { 
          accountId, 
          loginName: account.loginName,
          expiry: authSecret.token.accessToken_Expires,
          now: now.toISOString()
        });
        
        // Try to refresh the token if refresh token is available
        if (authSecret.token.refreshToken) {
          logger.auth('Attempting to refresh expired token');
          
          try {
            const { apiClient } = await import('../../utils/apiClient');
            const apiConstants = await import('../../constants/apiConstants');
            
            const refreshResponse = await apiClient.post(
              `${apiConstants.API_TOKENS_RENEW}`,
              {
                partitionKey: "sys:smart_matrix",
                refreshToken: authSecret.token.refreshToken
              }
            );

            if (refreshResponse.data.succeeded && refreshResponse.data.data) {
              // Update the stored account with new tokens
              authSecret.token.accessToken = refreshResponse.data.data.token.accessToken;
              authSecret.token.refreshToken = refreshResponse.data.data.token.refreshToken;
              authSecret.token.accessToken_Expires = new Date(Date.now() + (60 * 60 * 1000)).toISOString();
              
              // Update the stored account
              const updatedAccount = await AccountCredentialsHelper.createStoredAccount(
                authSecret,
                { rememberMe: true, persistenceDays: 30 },
                account.email
              );
              updatedAccount.id = account.id; // Keep the same ID
              await accountStorage.saveAccount(updatedAccount);
              
              logger.auth('Token refreshed successfully for account switch');
            } else {
              throw new Error('Token refresh failed');
            }
          } catch (refreshError) {
            logger.error('Failed to refresh token during account switch', refreshError);
            
            // Remove the invalid account from storage
            await accountStorage.removeAccount(accountId);
            
            store.dispatch(accountSwitchFailure({
              type: AuthErrorType.TokenInvalid,
              message: 'Token expired and refresh failed',
              userMessage: 'Account session has expired and could not be renewed. Please log in again.',
              code: 'TOKEN_REFRESH_FAILED',
              recoverable: false,
              retryable: false,
              timestamp: Date.now()
            }));
            
            return { success: false, error: 'Account session has expired and could not be renewed. Please log in again.' };
          }
        } else {
          // No refresh token available, remove the account
          await accountStorage.removeAccount(accountId);
          
          store.dispatch(accountSwitchFailure({
            type: AuthErrorType.TokenInvalid,
            message: 'Token expired and no refresh token available',
            userMessage: 'Account session has expired. Please log in again.',
            code: 'TOKEN_EXPIRED_NO_REFRESH',
            recoverable: false,
            retryable: false,
            timestamp: Date.now()
          }));
          
          return { success: false, error: 'Account session has expired. Please log in again.' };
        }
      }

      // Clear current tokens and set new ones using tokenManager
      tokenManager.clearTokens();
      
      // Set the account context BEFORE storing tokens
      tokenManager.setAccountContext(accountId);
      
      const tokenPair: TokenPair = {
        accessToken: authSecret.token.accessToken,
        refreshToken: authSecret.token.refreshToken || '',
        expiresAt: Date.now() + (authSecret.token.accessToken_LifeInMinutes * 60 * 1000),
        tokenType: 'Bearer'
      };

      logger.auth('Storing switched account tokens via tokenManager');
      tokenManager.storeTokens(tokenPair);

      // VERIFY token storage
      const storedTokens = tokenManager.getTokens();
      if (!storedTokens || storedTokens.accessToken !== authSecret.token.accessToken) {
        logger.error('Token storage verification failed during account switch');
        throw new Error('Token storage verification failed');
      }

      // Always fetch fresh user data with enhanced error handling
      try {
        await this.retrieveAndStoreUserInfo(authSecret.token.accessToken, 'accountSwitch');
      } catch (userInfoError) {
        logger.error('Failed to retrieve user info during account switch', userInfoError);
        
        // Check if it's a 401 error specifically
        const isUnauthorized = (userInfoError as any)?.response?.status === 401;
        
        if (isUnauthorized) {
          logger.warn('Account switch failed due to unauthorized access, removing invalid account', {
            accountId,
            loginName: account.loginName
          });
          
          // Remove the invalid account from storage
          await accountStorage.removeAccount(accountId);
          
          store.dispatch(accountSwitchFailure({
            type: AuthErrorType.TokenInvalid,
            message: 'Account access denied',
            userMessage: 'This account no longer has access to the system. It has been removed from your account list.',
            code: 'ACCOUNT_ACCESS_DENIED',
            recoverable: false,
            retryable: false,
            timestamp: Date.now()
          }));
          
          return { success: false, error: 'This account no longer has access to the system. It has been removed from your account list.' };
        } else {
          // Other types of errors
          store.dispatch(accountSwitchFailure({
            type: AuthErrorType.NetworkUnavailable,
            message: userInfoError instanceof Error ? userInfoError.message : 'Network error during account switch',
            userMessage: 'Failed to switch accounts due to a network error. Please try again.',
            code: 'ACCOUNT_SWITCH_NETWORK_ERROR',
            recoverable: false,
            retryable: true,
            timestamp: Date.now()
          }));
          
          return { success: false, error: 'Failed to switch accounts due to a network error. Please try again.' };
        }
      }

      // Update account metadata - SEQUENTIAL EXECUTION
      console.log('🔥 [authManager.switchAccount] About to updateLastUsed:', {
        accountIdParameter: accountId,
        accountObjectId: account.id
      });
      await accountStorage.updateLastUsed(accountId);
      
      // Set as active account in storage - SEQUENTIAL EXECUTION
      console.log('🔥 [authManager.switchAccount] About to setActiveAccountId:', {
        accountIdParameter: accountId
      });
      accountStorage.setActiveAccountId(accountId);

      // CRITICAL: Update Redux state with the new activeAccountId
      // This ensures the SignalR hook detects the account switch and reconnects
      const { setActiveAccount } = await import('../../store/slices/authSlice');
      
      console.log('🔥 [authManager.switchAccount] About to dispatch setActiveAccount:', {
        accountIdParameter: accountId,
        accountObjectId: account.id,
        usingAccountId: accountId
      });
      
      store.dispatch(setActiveAccount(accountId));

      logger.auth('Account switch completed successfully', { 
        accountId, 
        loginName: account.loginName,
        userName: account.userName 
      });
      
      return { success: true };
      
    } catch (error) {
      logger.error('Account switch failed', error);
      
      store.dispatch(accountSwitchFailure({
        type: AuthErrorType.WorkflowInterrupted,
        message: error instanceof Error ? error.message : 'Account switch error',
        userMessage: 'Failed to switch accounts. Please try again.',
        code: 'ACCOUNT_SWITCH_FAILED',
        recoverable: false,
        retryable: true,
        timestamp: Date.now()
      }));
      
      return { success: false, error: 'Failed to switch accounts. Please try again.' };
    }
  }

  async addAccount(credentials: LoginCredentials, options?: { rememberMe?: boolean; persistenceDays?: number }): Promise<AuthResult> {
    try {
      logger.auth('Starting add account process', { loginName: credentials.loginName });
      
      // Store the current session info before validating new credentials
      const currentState = store.getState();
      const currentUser = currentState.auth.user;
      tokenManager.getTokens();
      
      if (!currentUser) {
        return { success: false, error: 'No current user session found' };
      }
      
      logger.auth('Current user before adding account', { 
        currentUserId: currentUser.id, 
        currentUserName: currentUser.name,
        currentUserEmail: currentUser.email 
      });
      
      // Validate the new credentials by making a separate login API call
      // WITHOUT calling this.login() which would replace the current session
      const axios = (await import('axios')).default;
      const apiConstants = await import('../../constants/apiConstants');
      
      logger.auth('Validating new account credentials', { loginName: credentials.loginName });

      // Read cached public IP from localStorage (populated by apiClient.ts on page load)
      let clientIpForAddAccount: string | null = null;
      try { clientIpForAddAccount = localStorage.getItem('sm_public_ip'); } catch { /* ignore */ }

      const response = await axios.post(
        `${apiConstants.API_BASE_URL}${apiConstants.API_AUTH_STANDARD_LOGIN}`,
        {
          partitionKey: "sys:smart_matrix",
          loginName: credentials.loginName,
          password: credentials.password,
          returnUrl: window.location.origin + '/main'
        },
        clientIpForAddAccount ? { headers: { 'X-Client-IP': clientIpForAddAccount } } : undefined
      );

      const result = response.data;
      
      if (!result.succeeded || !result.data) {
        const error = `Login validation failed: ${result.messages?.join(', ') || 'Invalid credentials'}`;
        logger.auth('New account credential validation failed', { error });
        return { success: false, error: 'Invalid credentials for new account' };
      }
      
      // Get user info for the new account without changing current session
      const newAccountToken = result.data.token.accessToken;
      logger.auth('New account credentials validated, getting user info');
      
      const userInfoResponse = await axios.get(
        `${apiConstants.API_BASE_URL}${apiConstants.API_USERS_ME}`,
        {
          headers: {
            'Authorization': `Bearer ${newAccountToken}`
          }
        }
      );

      const userInfoResult = userInfoResponse.data;
      
      if (!userInfoResult.succeeded || !userInfoResult.data || !userInfoResult.data.tokenContent) {
        logger.auth('Failed to get user info for new account');
        return { success: false, error: 'Failed to retrieve user info for new account' };
      }

      const tokenContent = userInfoResult.data.tokenContent;
      
      // Extract user info for the new account
      let newAccountUserId = tokenContent.id || 
                            tokenContent.userId || 
                            tokenContent.userNameIdentifier || 
                            tokenContent.loginNameIdentifier;
      
      // If we got a string identifier, get the real numeric ID
      if (newAccountUserId && (typeof newAccountUserId === 'string' && isNaN(Number(newAccountUserId)))) {
        try {
          const userByLoginResponse = await axios.get(
            `${apiConstants.API_BASE_URL}${apiConstants.API_USERS_BY_LOGIN_NAME}`,
            {
              headers: {
                'Authorization': `Bearer ${newAccountToken}`
              },
              params: {
                partitionKey: "sys:smart_matrix",
                loginName: newAccountUserId
              }
            }
          );
          
          if (userByLoginResponse.data.succeeded && userByLoginResponse.data.data) {
            const userData = userByLoginResponse.data.data.user || userByLoginResponse.data.data;
            const numericUserId = userData.id || userData.userId;
            if (numericUserId !== undefined && numericUserId !== null && !isNaN(Number(numericUserId))) {
              newAccountUserId = numericUserId;
            }
          }
        } catch (lookupError) {
          logger.warn('Failed to lookup numeric user ID for new account', lookupError);
        }
      }
      
      const newAccountUserInfo = {
        id: String(Number(newAccountUserId)), // Ensure numeric ID
        email: tokenContent.email,
        name: tokenContent.name || tokenContent.givenName || credentials.loginName,
        loginName: credentials.loginName
      };
      
      logger.auth('New account user info retrieved', {
        newAccountUserId: newAccountUserInfo.id,
        newAccountEmail: newAccountUserInfo.email,
        newAccountName: newAccountUserInfo.name,
        newAccountLoginName: newAccountUserInfo.loginName
      });
      
      // Import account storage
      const { accountStorage, AccountCredentialsHelper } = await import('../../features/auth/utils/accountStorage');
      
      // Check for duplicate accounts using the new account's info
      const isDuplicate = await AccountCredentialsHelper.isDuplicateAccount(
        newAccountUserInfo.email, 
        accountStorage, 
        credentials.loginName
      );
      
      if (isDuplicate) {
        logger.auth('Account already exists, switching to existing account');
        const existingAccount = await AccountCredentialsHelper.findExistingAccount(
          newAccountUserInfo.email, 
          accountStorage, 
          credentials.loginName
        );
        
        if (existingAccount) {
          // Switch to the existing account and refresh its tokens
          await accountStorage.updateLastUsed(existingAccount.id);
          accountStorage.setActiveAccountId(existingAccount.id);
          
          // Set new tokens and apply preferences using tokenManager
          const tokenPair: TokenPair = {
            accessToken: result.data.token.accessToken,
            refreshToken: result.data.token.refreshToken || '',
            expiresAt: Date.now() + (result.data.token.accessToken_LifeInMinutes * 60 * 1000),
            tokenType: 'Bearer'
          };

          // Set the account context in tokenManager BEFORE storing tokens
          tokenManager.setAccountContext(existingAccount.id);
          
          logger.auth('Storing existing account tokens via tokenManager with account context');
          tokenManager.storeTokens(tokenPair);

          // VERIFY token storage
          const storedTokens = tokenManager.getTokens();
          if (!storedTokens || storedTokens.accessToken !== result.data.token.accessToken) {
            logger.error('Token storage verification failed for existing account');
            throw new Error('Token storage verification failed');
          }
          
          // Apply the account's user info and preferences
          await this.retrieveAndStoreUserInfo(result.data.token.accessToken, 'accountSwitch');
          
          // CRITICAL: Update Redux state with the activeAccountId
          // This ensures the SignalR hook detects the account switch and reconnects
          const { setActiveAccount } = await import('../../store/slices/authSlice');
          
          logger.auth('Dispatching setActiveAccount for existing account', {
            existingAccountId: existingAccount.id
          });
          
          store.dispatch(setActiveAccount(existingAccount.id));
          
          logger.auth('Existing account switched with preferences applied');
          return { success: true };
        }
      }
      
      // Create auth secret for the new account
      const authSecret = {
        loginName: credentials.loginName,
        userName: newAccountUserInfo.name,
        token: {
          accessToken: result.data.token.accessToken,
          accessToken_LifeInMinutes: 60,
          accessToken_Expires: new Date(Date.now() + (60 * 60 * 1000)).toISOString(),
          refreshToken: result.data.token.refreshToken,
          refreshToken_LifeInMinutes: 1440,
          refreshToken_Expires: new Date(Date.now() + (24 * 60 * 60 * 1000)).toISOString()
        }
      };
      
      // Set default options
      const persistenceOptions = {
        rememberMe: options?.rememberMe ?? true,
        persistenceDays: options?.persistenceDays ?? 30
      };
      
      // Create new stored account
      const storedAccount = await AccountCredentialsHelper.createStoredAccount(
        authSecret,
        persistenceOptions,
        newAccountUserInfo.email
      );
      
      // Save the new account
      await accountStorage.saveAccount(storedAccount);
      
      // SWITCH TO THE NEW ACCOUNT: Set the new account as active and apply its preferences
      logger.auth('Switching to newly added account', { 
        newAccountId: storedAccount.id,
        newAccountLoginName: storedAccount.loginName 
      });
      
      // Set new account tokens using tokenManager
      const tokenPair: TokenPair = {
        accessToken: result.data.token.accessToken,
        refreshToken: result.data.token.refreshToken || '',
        expiresAt: Date.now() + (result.data.token.accessToken_LifeInMinutes * 60 * 1000),
        tokenType: 'Bearer'
      };

      // Set as active account in storage FIRST
      accountStorage.setActiveAccountId(storedAccount.id);
      
      // Set the account context in tokenManager BEFORE storing tokens
      tokenManager.setAccountContext(storedAccount.id);
      
      logger.auth('Storing new account tokens via tokenManager with account context');
      tokenManager.storeTokens(tokenPair);

      // VERIFY token storage
      const storedTokens = tokenManager.getTokens();
      if (!storedTokens || storedTokens.accessToken !== result.data.token.accessToken) {
        logger.error('Token storage verification failed for new account');
        throw new Error('Token storage verification failed');
      }
      
      // Retrieve and apply the new account's user info and preferences
      await this.retrieveAndStoreUserInfo(result.data.token.accessToken, 'accountSwitch');
      
      // CRITICAL: Update Redux state with the new activeAccountId
      // This ensures the SignalR hook detects the account switch and reconnects
      const { setActiveAccount } = await import('../../store/slices/authSlice');
      
      logger.auth('Dispatching setActiveAccount for new account', {
        newAccountId: storedAccount.id
      });
      
      store.dispatch(setActiveAccount(storedAccount.id));
      
      logger.auth('Account added and switched successfully with preferences applied', { 
        newAccountId: storedAccount.id,
        newAccountLoginName: storedAccount.loginName,
        newAccountName: newAccountUserInfo.name
      });
      
      return { success: true };
      
    } catch (error) {
      logger.error('Add account failed', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to add account' };
    }
  }

  async removeAccount(accountId: string): Promise<AuthResult> {
    try {
      logger.auth('Starting account removal process', { accountId });
      
      // Get the account from storage first to check if it exists
      const { accountStorage } = await import('../../features/auth/utils/accountStorage');
      const account = await accountStorage.getAccount(accountId);
      
      if (!account) {
        logger.warn('Account not found for removal', { accountId });
        return { success: false, error: 'Account not found' };
      }

      // Check if this is the currently active account
      const currentActiveAccountId = accountStorage.getActiveAccountId();
      const isActiveAccount = currentActiveAccountId === accountId;
      
      // Remove the account from storage
      await accountStorage.removeAccount(accountId);
      
      logger.auth('Account removed successfully', { 
        accountId, 
        loginName: account.loginName,
        wasActive: isActiveAccount 
      });

      // If we removed the active account, we need to handle the session
      if (isActiveAccount) {
        logger.auth('Removed account was active, clearing session');
        
        // Clear current tokens using tokenManager
        tokenManager.clearTokens();

        // Check if there are other accounts available
        const remainingAccounts = await accountStorage.getAllAccounts();
        
        if (remainingAccounts.length > 0) {
          // Switch to the most recently used account
          const mostRecentAccount = remainingAccounts.reduce((prev, current) => 
            (current.lastUsed > prev.lastUsed) ? current : prev
          );
          
          logger.auth('Switching to most recent account after removal', { 
            newAccountId: mostRecentAccount.id,
            loginName: mostRecentAccount.loginName 
          });
          
          // Switch to the most recent account
          const switchResult = await this.switchAccount(mostRecentAccount.id);
          if (switchResult.success) {
            return { success: true };
          } else {
            // If switch fails, logout completely
            logger.warn('Failed to switch to remaining account, logging out');
            await this.logout();
            return { success: true };
          }
        } else {
          // No accounts left, logout completely
          logger.auth('No accounts remaining after removal, logging out');
          await this.logout();
          return { success: true };
        }
      }
      
      // Account removed but wasn't active, no session changes needed
      // Dispatch Redux action to notify UI components about the account removal
      store.dispatch(accountRemoved({ accountId, wasActive: false }));
      
      // Set lastOperation to trigger useAuth hook refresh
      const { setLastOperation } = await import('../../store/slices/authSlice');
      store.dispatch(setLastOperation('account_removed'));
      
      logger.auth('Dispatched accountRemoved action for inactive account', { accountId });
      
      return { success: true };
      
    } catch (error) {
      logger.error('Account removal failed', error, { accountId });
      return { success: false, error: error instanceof Error ? error.message : 'Failed to remove account' };
    }
  }

  getCurrentAccountId(): string | null {
    try {
      // Use synchronous storage access for immediate response
      if (typeof window !== 'undefined') {
        const { accountStorage } = require('../../features/auth/utils/accountStorage');
        const activeAccountId = accountStorage.getActiveAccountId();
        logger.auth('getCurrentAccountId called', { activeAccountId });
        return activeAccountId;
      }
      return null;
    } catch (error) {
      logger.error('Failed to get current account ID', error);
      return null;
    }
  }

  // Tenant methods - STUBS
  async switchTenant(_tenantId: string): Promise<AuthResult> {
    return { success: false, error: 'Tenant switching not yet implemented' };
  }

  async validateTenantAccess(_tenantId: string): Promise<boolean> {
    return true; // Default to allowing access
  }

  async setDefaultTenant(_tenantId: string): Promise<AuthResult> {
    return { success: false, error: 'Set default tenant not yet implemented' };
  }

  // Preferences methods - STUBS
  async updatePreferences(_preferences: Partial<UserPreferences>): Promise<AuthResult> {
    return { success: false, error: 'Preferences update not yet implemented' };
  }

  // Error handling methods - STUBS
  clearLastError(): void {
    // TODO: Implement error clearing
  }

  async retryLastOperation(): Promise<AuthResult> {
    return { success: false, error: 'Retry not yet implemented' };
  }

  // State management
  getCurrentUser(): User | null {
    const state = store.getState();
    return state.auth.user;
  }

  isAuthenticated(): boolean {
    const state = store.getState();
    return state.auth.isAuthenticated;
  }

  isInitialized(): boolean {
    const state = store.getState();
    return state.auth.isInitialized;
  }

  /**
   * Wait for authentication state to be properly set in Redux
   * This helps prevent race conditions after login
   */
  private async waitForAuthenticationState(maxWaitMs: number = 2000): Promise<boolean> {
    const startTime = Date.now();
    
    while (Date.now() - startTime < maxWaitMs) {
      const state = store.getState();
      const isAuthenticated = state.auth.isAuthenticated;
      const hasUser = !!state.auth.user;
      
      if (isAuthenticated && hasUser) {
        logger.auth('Authentication state confirmed', { 
          isAuthenticated, 
          hasUser, 
          userId: state.auth.user?.id,
          waitTime: Date.now() - startTime 
        });
        return true;
      }
      
      // Wait a bit before checking again
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    logger.warn('Authentication state not confirmed within timeout', { 
      maxWaitMs,
      currentState: {
        isAuthenticated: store.getState().auth.isAuthenticated,
        hasUser: !!store.getState().auth.user
      }
    });
    return false;
  }

  // Cleanup method
  cleanup(): void {
    // Clear all tokens using tokenManager
    tokenManager.clearTokens();
  }
}

// Export singleton instance
export const authManager = new AuthManager();